import type {
  IAuthenticateSMSCriteria,
  ICreateActivateEnrollmentByCriteria,
  IFetchBreachStatusCriteria,
  IFetchBreachStatusResponse,
  ITOTPAuthenticationRequest,
  IVerifyEnrollmentAndAuthorizeCriteria,
  IVerifyEnrollmentAndAuthorizeResponse,
  IWebAuthnAuthenticateCriteria,
  TChangePasswordCriteria,
} from "@mne-authentication/api";
import {
  type IAuthenticationData,
  type ILDAPAuthenticationRequest,
  type IPasswordAuthenticationRequest,
  authenticateLdap,
  authenticatePassword,
  authenticateSMS,
  authenticateTotp,
  authenticateWebauthn,
  createActivateEnrollmentByCriteria,
  fetchBreachStatusByCriteria,
  updateUserPassword,
  verifyEnrollmentAndAuthorize,
} from "@mne-authentication/api";
import type { ICreateActivateEnrollmentByCriteriaResponse } from "@mne-authentication/api/src/authentication/createActivateEnrollmentByCriteria";
import type { IUpdateUserPasswordResponse } from "@mne-authentication/api/src/authentication/updateUserPassword";
import type { IResultstash } from "@mne-core/api";

import * as ArgusUtils from "@mnemonic/pure-utils/ArgusUtils";
import { handleResponse } from "@mnemonic/redux-utils";

import type { IBaseApiParams } from "./baseApi";
import { baseApi } from "./baseApi";

const authenticateApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /* Mutations */
    authenticateLdap: build.mutation<IResultstash<IAuthenticationData>, IBaseApiParams<ILDAPAuthenticationRequest>>({
      queryFn: ({ criteria, metaOpts }) =>
        handleResponse(authenticateLdap(criteria, { apiUrl: ArgusUtils.getApiUrl() }), metaOpts),
      invalidatesTags: ["AuthenticationData"],
    }),
    authenticatePassword: build.mutation<
      IResultstash<IAuthenticationData>,
      IBaseApiParams<IPasswordAuthenticationRequest>
    >({
      queryFn: ({ criteria, metaOpts }) =>
        handleResponse(authenticatePassword(criteria, { apiUrl: ArgusUtils.getApiUrl() }), metaOpts),
      invalidatesTags: ["AuthenticationData"],
    }),
    updatePassword: build.mutation<IResultstash<IUpdateUserPasswordResponse>, IBaseApiParams<TChangePasswordCriteria>>({
      queryFn: ({ criteria, metaOpts }) =>
        handleResponse(
          updateUserPassword(criteria, {
            apiUrl: ArgusUtils.getApiUrl(),
            sessionKey: metaOpts?.sessionKey as unknown as string,
          }),
          metaOpts,
        ),
      invalidatesTags: ["AuthenticationData"],
    }),
    authenticateTotp: build.mutation<IResultstash<IAuthenticationData>, IBaseApiParams<ITOTPAuthenticationRequest>>({
      queryFn: ({ criteria, metaOpts }) =>
        handleResponse(authenticateTotp(criteria, { apiUrl: ArgusUtils.getApiUrl() }), metaOpts),
      invalidatesTags: ["AuthenticationData"],
    }),
    authenticateSMS: build.mutation<IResultstash<IAuthenticationData>, IBaseApiParams<IAuthenticateSMSCriteria>>({
      queryFn: ({ criteria, metaOpts }) =>
        handleResponse(authenticateSMS(criteria, { apiUrl: ArgusUtils.getApiUrl() }), metaOpts),
      invalidatesTags: ["AuthenticationData"],
    }),
    authenticateWebauthn: build.mutation<
      IResultstash<IAuthenticationData>,
      IBaseApiParams<IWebAuthnAuthenticateCriteria>
    >({
      queryFn: ({ criteria, metaOpts }) =>
        handleResponse(authenticateWebauthn(criteria, { apiUrl: ArgusUtils.getApiUrl() }), metaOpts),
      invalidatesTags: ["AuthenticationData"],
    }),
    activateEnrollment: build.mutation<
      IResultstash<ICreateActivateEnrollmentByCriteriaResponse>,
      IBaseApiParams<ICreateActivateEnrollmentByCriteria>
    >({
      queryFn: ({ criteria, metaOpts }) =>
        handleResponse(createActivateEnrollmentByCriteria(criteria, { apiUrl: ArgusUtils.getApiUrl() }), metaOpts),
    }),
    fetchBreachStatus: build.mutation<
      IResultstash<IFetchBreachStatusResponse>,
      IBaseApiParams<IFetchBreachStatusCriteria>
    >({
      queryFn: ({ criteria, metaOpts }) =>
        handleResponse(
          fetchBreachStatusByCriteria(criteria, {
            apiUrl: ArgusUtils.getApiUrl(),
            sessionKey: metaOpts?.sessionKey as unknown as string,
          }),
          metaOpts,
        ),
    }),
    verifyAndAuthorize: build.mutation<
      IResultstash<IVerifyEnrollmentAndAuthorizeResponse>,
      IBaseApiParams<IVerifyEnrollmentAndAuthorizeCriteria>
    >({
      queryFn: ({ criteria, metaOpts }) =>
        handleResponse(verifyEnrollmentAndAuthorize(criteria, { apiUrl: ArgusUtils.getApiUrl() }), metaOpts),
    }),
  }),
});

export const {
  useActivateEnrollmentMutation,
  useAuthenticateLdapMutation,
  useAuthenticatePasswordMutation,
  useAuthenticateSMSMutation,
  useAuthenticateTotpMutation,
  useAuthenticateWebauthnMutation,
  useFetchBreachStatusMutation,
  useUpdatePasswordMutation,
  useVerifyAndAuthorizeMutation,
} = authenticateApi;

export default authenticateApi;
